import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OnboardingPages, Pages } from '../../../common/src/pages.ts';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { useHousehold } from '../data/use_household.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { isEventIsland } from '../pages/event_island.tsx';
import { navigateToOnboardingPage } from '../pages/onboarding/common.tsx';
import { getPageFromLocation } from './path.ts';
import { isHymnUrl } from './shared.tsx';

// Pages where a household without a chosen user is expected, so choose user must not interrupt:
// onboarding drives its own navigation (e.g. HouseholdFirstName creates the first user), the invite
// pages ask who the visitor is themselves, and event and song links are for listening.
function mayChooseUserHere(location: ReturnType<typeof useLocation>): boolean {
  if (window.location.href.includes('onboarding')) {
    return false;
  }
  const page = getPageFromLocation(location);
  if (page === Pages.AdminInvite || page === Pages.HouseholdInvite || page === Pages.DeprecatedHouseholdInvite) {
    return false;
  }
  if (isEventIsland(page, location.search) || isHymnUrl(location.search)) {
    return false;
  }
  return true;
}

// The safety net for a household in the app without a chosen user, who then has no profile menu:
// ask who they are. It checks on every page change as well as when the household or user changes,
// since a household that signed in to subscribe (GitHub issue #3551) reaches the app without ever
// passing choose user, and nothing it watched changed on the way in.
export function useSelectUser() {
  const [activeUserToken] = useLocalStorage(LocalStorageKey.ActiveUserToken);
  const { household } = useHousehold();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!activeUserToken && household && mayChooseUserHere(location)) {
      void navigateToOnboardingPage(navigate, OnboardingPages.ChooseUser);
    }
  }, [activeUserToken, household, location, navigate]);
}
